import {useCallback, useEffect, useRef, useState} from 'react';
import {draftStorage} from '../lib/draftStorage';
import {isStale} from '../lib/draftState';

// Nothing typed into a form is lost by leaving, reloading or closing the app. A form keeps its
// values through this hook, keyed by what is being edited ("edit:<postId>", "place:new"):
// changes are written to draft storage (debounced), restored on the next mount while they
// still differ from the saved thing, and cleared on save or discard.
//
//   const form = useFormDraft('edit:' + post.id, fromPost(post), {label, route});
//   form.values / form.set({text}) / form.dirty / form.restored / form.discard() / form.clear()

export interface FormDraftMeta {
    /** Banner text, e.g. "Unsaved changes to “Six seedlings in”". */
    label: string;
    /** Where Continue goes. */
    route: string;
    /** false for small sheets that keep a draft quietly without a banner entry. */
    listed?: boolean;
}

const sameValues = <T extends object>(a: T, b: T): boolean => {
    const ra = a as Record<string, unknown>;
    const rb = b as Record<string, unknown>;
    return Object.keys({...ra, ...rb}).every(k => ra[k] === rb[k]);
};

export function useFormDraft<T extends object>(key: string, initial: T, meta: FormDraftMeta) {
    const [values, setValuesState] = useState<T>(initial);
    const [ready, setReady] = useState(false);
    const [restored, setRestored] = useState(false);
    const initialRef = useRef(initial);
    const metaRef = useRef(meta);
    const startedAt = useRef(new Date().toISOString());
    const timer = useRef<number | null>(null);
    initialRef.current = initial;
    metaRef.current = meta;

    // Restore once.
    useEffect(() => {
        let cancelled = false;
        void draftStorage.load(key).then(record => {
            if (cancelled) return;
            if (record && !isStale(record.updatedAt)) {
                const merged = {...initialRef.current, ...(record.fields as Partial<T>)};
                if (!sameValues(merged, initialRef.current)) {
                    setValuesState(merged);
                    setRestored(true);
                    startedAt.current = record.startedAt;
                }
            } else if (record) {
                void draftStorage.remove(key);
            }
            setReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, [key]);

    const persist = useCallback(
        (next: T) => {
            if (timer.current) window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => {
                timer.current = null;
                if (sameValues(next, initialRef.current)) {
                    void draftStorage.remove(key);
                    return;
                }
                const m = metaRef.current;
                void draftStorage.save({
                    key,
                    label: m.label,
                    route: m.route,
                    listed: m.listed ?? true,
                    fields: next as unknown as Record<string, unknown>,
                    photos: [],
                    startedAt: startedAt.current,
                    updatedAt: new Date().toISOString()
                });
            }, 300);
        },
        [key]
    );

    const set = useCallback(
        (patch: Partial<T> | ((prev: T) => T)) => {
            setValuesState(prev => {
                const next = typeof patch === 'function' ? patch(prev) : {...prev, ...patch};
                persist(next);
                return next;
            });
        },
        [persist]
    );

    const clear = useCallback(() => {
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = null;
        setRestored(false);
        void draftStorage.remove(key);
    }, [key]);

    const discard = useCallback(() => {
        clear();
        setValuesState(initialRef.current);
    }, [clear]);

    useEffect(() => () => {
        if (timer.current) window.clearTimeout(timer.current);
    }, []);

    return {values, set, ready, restored, dirty: !sameValues(values, initial), clear, discard};
}
